// ---------------------------------------------------------------------------
// UserInTheLoop Admin SDK — Main entry point
// ---------------------------------------------------------------------------

import type { UITLConfig } from './types';
import { UITLApiClient } from './api';

// ---------------------------------------------------------------------------
// React component re-exports (lazy — files may not exist yet)
// ---------------------------------------------------------------------------

export { UITLProvider, useUITL } from './components/UITLProvider';
export type { UITLProviderProps, UITLContextValue, UITLResolvedTheme } from './components/UITLProvider';
export { ChatWidget } from './components/ChatWidget';
export type { ChatWidgetProps } from './components/ChatWidget';
export { FeedbackButton } from './components/FeedbackButton';
export type { FeedbackButtonProps } from './components/FeedbackButton';
export { UniversalFeedbackButton } from './components/UniversalFeedbackButton';
export type {
  UniversalFeedbackButtonProps,
  FeedbackSubmitData,
  FeedbackSubmitResult,
  FeedbackUser,
  FeedbackType,
} from './components/UniversalFeedbackButton';
export { AnnouncementBanner } from './components/AnnouncementBanner';
export type { AnnouncementBannerProps } from './components/AnnouncementBanner';
export { ImpersonationBar } from './components/ImpersonationBar';
export type { ImpersonationBarProps } from './components/ImpersonationBar';
export { FeatureGate } from './components/FeatureGate';
export type { FeatureGateProps } from './components/FeatureGate';
export { AdminPanel } from './components/AdminPanel';
export type { AdminPanelProps } from './components/AdminPanel';

// ---------------------------------------------------------------------------
// Type & class re-exports
// ---------------------------------------------------------------------------

export * from './types';
export { UITLApiClient, UITLApiError } from './api';

// ---------------------------------------------------------------------------
// Vanilla JS initialiser (for CDN / non-React usage)
// ---------------------------------------------------------------------------

/**
 * Initialise the UITL Admin SDK with the given configuration.
 *
 * Returns an {@link UITLApiClient} instance that can be used for imperative
 * API calls (chat, feedback, announcements, guardrails, etc.).
 *
 * Usage (script tag):
 * ```html
 * <script src="https://cdn.userintheloop.com/sdk/uitl-admin-sdk.umd.js"></script>
 * <script>
 *   const client = UITLAdmin.init({ apiKey: 'pk_live_...' });
 *   client.getAnnouncements().then(console.log);
 * </script>
 * ```
 */
export function init(config: UITLConfig): UITLApiClient {
  if (!config.apiKey) {
    throw new Error('[UITL SDK] "apiKey" is required in config');
  }

  const client = new UITLApiClient(config);

  // Fire the ready callback (async so it doesn't block the return value)
  if (config.onReady) {
    // Fetch remote config first, then signal ready
    client
      .getConfig()
      .then(() => config.onReady?.())
      .catch((err) => {
        console.error('[UITL SDK] Failed to fetch remote config:', err);
        // Still signal ready so the caller isn't left hanging
        config.onReady?.();
      });
  }

  return client;
}

// ---------------------------------------------------------------------------
// Vanilla JS widget exports (no React dependency)
// ---------------------------------------------------------------------------

export { VanillaChatWidget } from './widgets/chat-widget';
export { VanillaFeedbackWidget } from './widgets/feedback-widget';
export { VanillaFeedbackWidgetV2 } from './widgets/feedback-widget-v2';
export { VanillaAnnouncementWidget } from './widgets/announcement-widget';

// ---------------------------------------------------------------------------
// Global namespace for CDN / UMD consumers
// ---------------------------------------------------------------------------

import { VanillaChatWidget as _VCW } from './widgets/chat-widget';
import { VanillaFeedbackWidget as _VFW } from './widgets/feedback-widget';
import { VanillaFeedbackWidgetV2 as _VFW2 } from './widgets/feedback-widget-v2';
import { VanillaAnnouncementWidget as _VAW } from './widgets/announcement-widget';

if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).UITLAdmin = {
    init,
    UITLApiClient,
    VanillaChatWidget: _VCW,
    VanillaFeedbackWidget: _VFW,
    VanillaFeedbackWidgetV2: _VFW2,
    VanillaAnnouncementWidget: _VAW,
  };
}
